import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react';

/**
 * Keys the Sabre keyboard relabels. The apostrophe key carries the cross of
 * Lorraine, which the host displays as ¥.
 */
const sabreKeys: Record<string, string> = {
  "'": '¥',
};

const toSabre = (text: string) => text.replace(/'/g, sabreKeys["'"]);

interface CommandBarProps {
  pcc: string;
  busy: boolean;
  /** Entries already sent in this work area, oldest first. */
  history: string[];
  onSubmit: (entry: string) => void;
  onShowHistory: () => void;
  onShowHelp: () => void;
}

/**
 * The host command line. Arrow Up and Down walk back through what has already
 * been sent, which is how an agent repeats a search with one qualifier changed.
 */
export function CommandBar({ pcc, busy, history, onSubmit, onShowHistory, onShowHelp }: CommandBarProps) {
  const [entry, setEntry] = useState('');
  const [recallIndex, setRecallIndex] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Where the caret belongs after a relabelled key rewrites the entry.
  const pendingCaret = useRef<number | null>(null);

  useLayoutEffect(() => {
    if (pendingCaret.current === null) return;
    inputRef.current?.setSelectionRange(pendingCaret.current, pendingCaret.current);
    pendingCaret.current = null;
  }, [entry]);

  // The command line takes focus on arrival and keeps it after every entry.
  useEffect(() => {
    if (!busy) inputRef.current?.focus();
  }, [busy]);

  const submit = () => {
    const value = entry.trim();
    if (!value || busy) return;
    onSubmit(value);
    setEntry('');
    setRecallIndex(null);
  };

  const recall = (step: number) => {
    if (history.length === 0) return;
    const current = recallIndex ?? history.length;
    const next = Math.min(Math.max(current + step, 0), history.length);
    setRecallIndex(next === history.length ? null : next);
    setEntry(next === history.length ? '' : history[next]);
  };

  const typeSabreKey = (input: HTMLInputElement, glyph: string) => {
    const start = input.selectionStart ?? entry.length;
    const end = input.selectionEnd ?? start;
    pendingCaret.current = start + glyph.length;
    setEntry(entry.slice(0, start) + glyph + entry.slice(end));
    setRecallIndex(null);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    const glyph = sabreKeys[event.key];
    if (glyph && !event.ctrlKey && !event.metaKey && !event.altKey) {
      event.preventDefault();
      typeSabreKey(event.currentTarget, glyph);
      return;
    }

    switch (event.key) {
      case 'Enter':
        event.preventDefault();
        submit();
        break;
      case 'ArrowUp':
        event.preventDefault();
        recall(-1);
        break;
      case 'ArrowDown':
        event.preventDefault();
        recall(1);
        break;
      case 'Escape':
        event.preventDefault();
        setEntry('');
        setRecallIndex(null);
        break;
      default:
        break;
    }
  };

  return (
    <div className="cmdbar">
      <span className="cmdbar__agent" aria-hidden="true">
        <svg viewBox="0 0 24 24" width="22" height="22">
          <circle cx="12" cy="7.5" r="3.8" fill="currentColor" />
          <path d="M4.6 20.5a7.4 7.4 0 0 1 14.8 0Z" fill="currentColor" />
        </svg>
      </span>

      <div className="cmdbar__field">
        <input
          ref={inputRef}
          className="cmdbar__input"
          type="text"
          autoComplete="off"
          spellCheck={false}
          aria-label="Host command"
          placeholder="TYPE COMMAND HERE"
          value={entry}
          disabled={busy}
          onChange={(event) => setEntry(toSabre(event.target.value))}
          onKeyDown={handleKeyDown}
        />

        <button type="button" className="cmdbar__popout" title="Not implemented" disabled>
          <svg viewBox="0 0 24 24" width="17" height="17" aria-hidden="true">
            <path
              d="M14 4h6v6M20 4l-8.5 8.5M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      {/* Send stays live whatever is on the command line, as on the host; an
          empty entry simply does nothing. */}
      <button type="button" className="cmdbar__send" onClick={submit} disabled={busy}>
        {busy ? 'Sending' : 'Send'}
      </button>

      <button type="button" className="cmdbar__icon" title="Command history" onClick={onShowHistory}>
        <svg viewBox="0 0 24 24" width="19" height="19" aria-hidden="true">
          <path
            d="M12 7.5V12l3.5 2M3.6 12a8.4 8.4 0 1 0 2.6-6.1M3.6 4.8V9.3h4.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      <button type="button" className="cmdbar__link" title="Not implemented" disabled>
        PF Keys
      </button>

      <button type="button" className="cmdbar__link" onClick={onShowHelp}>
        {/* A filled wand and three sparkles, as the host draws it. */}
        <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
          <path
            d="M7.5 5.6 10 7 8.6 4.5 10 2 7.5 3.4 5 2l1.4 2.5L5 7zm12 9.8L17 14l1.4 2.5L17 19l2.5-1.4L22 19l-1.4-2.5L22 14zM22 2l-2.5 1.4L17 2l1.4 2.5L17 7l2.5-1.4L22 7l-1.4-2.5zm-7.63 5.29a1 1 0 0 0-1.41 0L1.29 18.96a1 1 0 0 0 0 1.41l2.34 2.34a1 1 0 0 0 1.41 0L16.7 11.05a1 1 0 0 0 0-1.41zm-1.03 5.49-2.12-2.12 2.44-2.44 2.12 2.12z"
            fill="currentColor"
          />
        </svg>
        Command Helper
      </button>

      <span className="cmdbar__pcc">CERT-{pcc || '----'}</span>
    </div>
  );
}
